import { useQuery } from '@tanstack/react-query';
import { dataCentralApi } from '@/api/client';

/**
 * Career-path stages for an employee — the "Working Process" timeline.
 *
 * `GET /employees/:employeeID/career-paths` (datacentral-api-v4
 * `modules/employee/employee.career_paths`). Read-only self-service view; the
 * write actions (create / transition / extend / notes) are HR-admin only.
 * Envelope is `{ message, results }` where `results` is null when the employee
 * has no recorded stages.
 */

/** Lifecycle stage of a career-path entry (backend enum). */
export type CareerPathStage = 'intern' | 'probation' | 'official' | 'offboard';

/** A note attached to a career-path stage. */
export interface CareerPathNote {
  note_content: string;
  created_by_name?: string | null;
  created_at?: string | null;
}

/** One career-path stage row as returned by the list endpoint. */
export interface CareerPathStageRow {
  career_path_id: number;
  employee_id: number;
  career_path_stage: CareerPathStage | string;
  career_path_start_date: string | null;
  career_path_end_date: string | null;
  career_path_note: string | null;
  created_at?: string | null;
  notes?: CareerPathNote[] | null;
}

interface CareerPathsResponse {
  message: string;
  results: CareerPathStageRow[] | null;
}

/**
 * `GET /employees/:employeeID/career-paths` — the stage timeline for one
 * employee. Pass the current user's `employee_id` (resolve via
 * `useCurrentEmployee`). Disabled until an id is available.
 */
export function useCareerPaths(employeeId: number | undefined) {
  return useQuery({
    queryKey: ['employees', 'career-paths', employeeId],
    enabled: !!employeeId,
    queryFn: async (): Promise<CareerPathStageRow[]> => {
      const res = await dataCentralApi.get<CareerPathsResponse>(
        `/employees/${employeeId}/career-paths`,
      );
      return res.data.results ?? [];
    },
  });
}
